import $ from "utils/net";

import { ComponentType } from "preact";

/**
 * API per i gateway di pagamento
 */
export class PaymentGateway {
    constructor(
        private readonly endpoint: string
    ) { }

    async requestDeposit(provider: string, processor: string, amount: string): Promise<DepositResponse> {
        const url = this.endpoint + "/ussd_payments/requestDeposit/" + provider + '/' + processor;
        const response: DepositResponse = await new Promise((res, rej) => $.post(url, {
            body: {
                amount: amount,
            },
            json: true
        }, (err: string | null, data: DepositResponse) => err ? rej(err) : res(data)));
        return response;
    }

    async requestWithdraw(provider: string, processor: string, amount: string): Promise<WithdrawResponse> {
        const url = this.endpoint + "/ussd_payments/requestWithdraw/" + provider + '/' + processor;
        const response: WithdrawResponse = await new Promise((res, rej) => $.post(url, {
            body: {
                amount: amount,
            },
            json: true
        }, (err: any, data: WithdrawResponse) => err ? rej(err) : res(data)));
        return response;
    }

    async requestServicesList(): Promise<ProcessorsListResponse> {
        const url = this.endpoint + "/ussd_payments/allowedServices";
        const request: ProcessorsListResponse = await new Promise((res, rej) => {
            return $.getJSON(url, (err: any, data: {data: ProcessorsListResponse, status: string}) => err ? rej(err) : res(data.data));
        });
        return request;
    }

    async getP2PIframe(initialPath: "offers"|"my-purchases"|"withdraw"|"my-offers"): Promise<{iframe: string}> {
        const url = this.endpoint + "/ptop_payments/getIframe/"+initialPath;
        const request: {iframe: string} = await new Promise((res, rej) => {
            return $.getJSON(url, (err: any, data: {iframe: string}) => err ? rej(err) : res(data));
        });
        return request;
    }
}

/**
 * Quando lo avvii, chiama ogni tre secondi statusUrl (chiamata GET senza credenziali)
 */
export class TransactionStatusPoller {
    private intervalId: ReturnType<typeof setInterval> | null = null;
    private listeners: Set<(status: PaymentNotification) => void> = new Set();

    constructor(
        private readonly statusUrl: string
    ) {}

    public start() {
        if (this.intervalId) return; // Prevent multiple intervals
        this.intervalId = setInterval(async () => {
            try {
                const response = await fetch(this.statusUrl, {credentials: 'omit'});
                if (!response.ok) 
                    throw response;
                const status: PaymentNotification = await response.json();
                if (status.status == PaymentStatus.PENDING) {
                    // Ignora.
                    return;
                }
                this.notifyListeners(status);
                this.stop();
            } catch (error) {
                console.error("Error polling transaction status:", error);
            }
        }, 3000); // Poll every 3 seconds
    }

    public stop() {
        if (this.intervalId) {
            clearInterval(this.intervalId);
            this.intervalId = null;
        }
    }

    public addListener(cb: (status: PaymentNotification) => void) {
        this.listeners.add(cb);
    }

    public removeListener(cb: (status: PaymentNotification) => void) {
        this.listeners.delete(cb);
    }

    private notifyListeners(status: PaymentNotification) {
        for (const listener of this.listeners) {
            listener(status);
        }
    }
}

export interface ErrorMsg {
    source: string,
    msg: string
}

export interface DepositResponse {
    status: "success" | "error",
    error?: ErrorMsg,
    data: {
        redirecturl?: string,
        statusUrl?: string
    }
}
export interface WithdrawResponse {
    status: "success" | "error",
    error?: ErrorMsg,
    data: {
        redirecturl?: string,
        statusUrl?: string
    }
}

export enum PaymentStatus {
    PENDING = "PENDING",
    FAILED = "FAILED",
    SUCCESS = "SUCCESS",
}

export type PendingPayment = {
    status: PaymentStatus.PENDING,
};
export type FailedPayment = {
    status: PaymentStatus.FAILED,
    error: ErrorMsg
};
export type SuccessPayment = {
    status: PaymentStatus.SUCCESS,
    amount: number,
    currency: string,
};

/**
 * Ritornato da statusUrl durante il polling
 */
export type PaymentNotification = (PendingPayment | FailedPayment | SuccessPayment) & {
    tenant: string,
    txId: string,
    teantRef: string
}

export interface ProcessorRef {
    provider: string,
    processor: string,
    name: string,
    bonusPerc?: number
}
export enum ProcessorTypes {
    withdraw = 'withdraw',
    deposit = 'deposit',
}
export type ProcessorComponent = ComponentType<ProcessorRef>;
type ProcessorComponents = Record<ProcessorTypes, ProcessorComponent>;

export interface ProcessorDesc extends ProcessorRef, ProcessorComponents {
}
export type ProcessorsListResponse = { [key in ProcessorTypes]: ProcessorDesc[] };
